import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilter, faCalendarAlt, faEraser } from "@fortawesome/free-solid-svg-icons";
import { Row, Col, Form } from "react-bootstrap";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import { useTranslation } from "react-i18next";
import Button from "components/base/Button";
import { InvoiceType } from "services/invoiceService";

interface InvoiceFilterPanelProps {
  fromDate: Date | undefined;
  setFromDate: (date: Date | undefined) => void;
  toDate: Date | undefined;
  setToDate: (date: Date | undefined) => void;
  applyFilter: () => void;
  typeFilter: InvoiceType | string;
  setTypeFilter: (type: InvoiceType | string) => void;
  clearAllFilters: () => void;
}

const InvoiceFilterPanel: React.FC<InvoiceFilterPanelProps> = ({
  fromDate,
  setFromDate,
  toDate,
  setToDate,
  applyFilter,
  typeFilter,
  setTypeFilter,
  clearAllFilters
}) => {
  // Local state for the type filter to avoid triggering API calls on every change
  const [localTypeFilter, setLocalTypeFilter] = useState<InvoiceType | string>(typeFilter);
  
  // Update local state when the prop changes
  useEffect(() => {
    setLocalTypeFilter(typeFilter);
  }, [typeFilter]);
  
  // Handle apply filter with all filter changes
  const handleApplyFilter = () => {
    // Always apply the local type filter to the parent state
    // to ensure changes are propagated
    setTypeFilter(localTypeFilter);
    
    // Call the parent apply filter function
    applyFilter();
    
    console.log("Applied type filter:", localTypeFilter); // Debug logging
  };
  const { t } = useTranslation();

  return (
    <div className="card mb-4 border-0 rounded-3" style={{ overflow: 'visible' }}>
      <div className="card-body bg-body p-4">
        <Row>
          <Col xs={12}>
            <div className="d-flex flex-wrap align-items-end gap-3">
              {/* Type filter */}
              <div style={{ minWidth: '280px'}}>
                <label className="form-label fw-semibold text-body mb-2">{t("invoices.tableHeaders.type")}</label>
                <Form.Select 
                  value={localTypeFilter}
                  onChange={(e) => {
                    console.log("Dropdown changed to:", e.target.value);
                    setLocalTypeFilter(e.target.value);
                    // Apply filter immediately when dropdown changes
                    setTypeFilter(e.target.value);
                    applyFilter();
                  }}
                  className="form-control"
                >
                  <option value="">{localTypeFilter === "" ? t("selectOption") : "All"}</option>
                  <option value={InvoiceType.Logistic}>{t("invoices.types.logistic")}</option>
                  <option value={InvoiceType.Penalty}>{t("invoices.types.penalty")}</option>
                </Form.Select>
              </div>
              
              {/* From date */}
              <div style={{ minWidth: '280px'}}>
                <label className="form-label fw-semibold text-body mb-2">{t("invoices.fromDate")}</label>
                <div className="input-group">
                  <span className="input-group-text bg-body-tertiary border-end-0">
                    <FontAwesomeIcon icon={faCalendarAlt} className="text-body-secondary" />
                  </span>
                  <Flatpickr
                    className="form-control border-start-0 ps-1"
                    value={fromDate || undefined}
                    onChange={([date]) => setFromDate(date)}
                    options={{ 
                      dateFormat: "d/m/Y",
                      allowInput: true
                    }}
                    placeholder={t("invoices.selectFromDate")}
                  />
                </div>
              </div>
              
              {/* To date */}
              <div style={{ minWidth: '280px'}}>
                <label className="form-label fw-semibold text-body mb-2">{t("invoices.toDate")}</label>
                <div className="input-group">
                  <span className="input-group-text bg-body-tertiary border-end-0">
                    <FontAwesomeIcon icon={faCalendarAlt} className="text-body-secondary" />
                  </span>
                  <Flatpickr
                    className="form-control border-start-0 ps-1"
                    value={toDate || undefined}
                    onChange={([date]) => setToDate(date)}
                    options={{ 
                      dateFormat: "d/m/Y",
                      allowInput: true
                    }}
                    placeholder={t("invoices.selectToDate")}
                  />
                </div>
              </div>
              
              {/* Clear filter button */}
              <div>
                <Button
                  variant="outline-secondary"
                  onClick={clearAllFilters}
                  className="rounded-2 px-3 py-2"
                >
                  <FontAwesomeIcon icon={faEraser} className="me-1" />
                  {t("invoices.clearFilter")}
                </Button>
              </div>
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default InvoiceFilterPanel;
